import React, { useCallback } from 'react';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { LinkItem, Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	CUSTOM_ITEM_BUTTON,
	CUSTOM_ITEM_ACTION,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { APP } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	LEVEL_THREE,
	LEVEL_TWO,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_APPS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import type { OnCloseProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { MoreNavMenuButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { MoreNavMenuButtonMenuItemsProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import messages from './messages.tsx';

type AppActionsProps = { settingsUrl: string; appId: string } & OnCloseProps;

type NavMenuItemsProps = MoreNavMenuButtonMenuItemsProps & {
	settingsUrl: string;
	onTrackClick: () => void;
};

const NavMenuItems = ({ onClose, onTrackClick, settingsUrl }: NavMenuItemsProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
			onTrackClick();
			onClose?.(event);
		},
		[onClose, onTrackClick],
	);

	return (
		<Section>
			<LinkItem
				iconBefore={<SettingsIcon label="" color={token('color.icon')} />}
				href={settingsUrl}
				target="_blank"
				onClick={handleClick}
			>
				{formatMessage(messages.appSettings)}
			</LinkItem>
		</Section>
	);
};

export function AppActions({
	settingsUrl,
	appId,
	onClose,
}: AppActionsProps): React.JSX.Element | null {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireOpenEvent = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
			attributes: {
				level: LEVEL_TWO,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: 'meatballDropdown',
				section: 'yourApps',
			},
		});
	}, [createAnalyticsEvent]);

	const fireClickEvent = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
			attributes: {
				level: LEVEL_THREE,
				componentType: CUSTOM_ITEM_ACTION,
				parentItemId: appId,
				parentItemType: APP,
				itemId: 'appSettings',
				section: 'yourApps',
			},
		});
	}, [createAnalyticsEvent, appId]);

	const MenuItems = useCallback(
		(props: MoreNavMenuButtonMenuItemsProps) => (
			<NavMenuItems
				onClose={props.onClose}
				onTrackClick={fireClickEvent}
				settingsUrl={settingsUrl}
			/>
		),
		[fireClickEvent, settingsUrl],
	);

	if (!settingsUrl) {
		return null;
	}

	return <MoreNavMenuButton MenuItems={MenuItems} onOpen={fireOpenEvent} onClose={onClose} />;
}
